import { Component, OnInit, inject, signal } from '@angular/core';

import { CommonModule } from '@angular/common';

import { FormsModule } from '@angular/forms';



import { MatFormFieldModule } from '@angular/material/form-field';

import { MatInputModule } from '@angular/material/input';

import { MatSelectModule } from '@angular/material/select';

import { MatButtonModule } from '@angular/material/button';

import { MatIconModule } from '@angular/material/icon';

import { MatSlideToggleModule } from '@angular/material/slide-toggle';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { MatTableModule } from '@angular/material/table';

import { MatDividerModule } from '@angular/material/divider';



import { ApiService } from '../../core/services/api.service';

import { AutomationService } from './automation.service';

import {

  AUTOMATION_ACTION_OPTIONS,

  AUTOMATION_CONDITION_FIELDS,

  AUTOMATION_CONDITION_OPS,

  AUTOMATION_TRIGGER_OPTIONS,

  AutomationConditionRow,

  AutomationRuleDto,

  defaultConditionForTrigger,

  PmAutomationExecutionLogDto,

  resolveBackendTriggerType

} from './automation.models';



interface ProjectRow { id: string; name: string; }



@Component({

  selector: 'app-automation',

  standalone: true,

  imports: [

    CommonModule, FormsModule,

    MatFormFieldModule, MatInputModule, MatSelectModule, MatButtonModule,

    MatIconModule, MatSlideToggleModule, MatProgressSpinnerModule, MatTableModule,

    MatDividerModule

  ],

  templateUrl: './automation.component.html',

  styleUrl: './automation.component.scss'

})

export class AutomationComponent implements OnInit {

  private readonly automation = inject(AutomationService);

  private readonly api = inject(ApiService);



  readonly loading = signal(false);

  readonly saving = signal(false);

  readonly error = signal<string | null>(null);

  readonly rules = signal<AutomationRuleDto[]>([]);

  readonly executions = signal<PmAutomationExecutionLogDto[]>([]);

  readonly projects = signal<ProjectRow[]>([]);

  readonly selectedProjectId = signal<string>('');



  ruleName = '';

  ruleDescription = '';

  triggerType = 0;

  actionType = 'AssignEmployee';

  assignEmployeeId = '';

  notifyUserId = '';

  actionFieldKey = '';

  actionFieldValue = '';

  actionStatus = '';

  actionSprintId = '';

  actionEmail = '';

  actionWebhookUrl = '';

  actionReminderHours = '24';

  conditions: AutomationConditionRow[] = [];



  readonly triggerOptions = signal([...AUTOMATION_TRIGGER_OPTIONS]);

  readonly actionOptions = signal([...AUTOMATION_ACTION_OPTIONS]);

  readonly conditionFields = signal([...AUTOMATION_CONDITION_FIELDS]);

  readonly conditionOps = signal([...AUTOMATION_CONDITION_OPS]);

  readonly columns = ['name', 'trigger', 'action', 'enabled', 'actions'];

  readonly executionColumns = ['ruleName', 'triggerTypeLabel', 'actionType', 'success', 'message', 'occurredAt'];



  ngOnInit(): void {

    this.automation.getMetadata().subscribe({

      next: (meta) => {

        if (meta.triggers?.length) this.triggerOptions.set(meta.triggers);

        if (meta.actions?.length) this.actionOptions.set(meta.actions);

        if (meta.conditionFields?.length) this.conditionFields.set(meta.conditionFields);

        if (meta.conditionOperators?.length) this.conditionOps.set(meta.conditionOperators);

      },

      error: () => { /* keep hardcoded defaults */ }

    });



    this.api.get<{ items?: ProjectRow[] } | ProjectRow[]>('v1/projects', { take: 100 }).subscribe({

      next: (res) => {

        const items = Array.isArray(res) ? res : (res.items ?? []);

        this.projects.set(items);

        if (items.length) {

          this.selectedProjectId.set(items[0].id);

          this.loadRules();

          this.loadExecutions();

        }

      },

      error: () => this.projects.set([])

    });

  }



  onProjectChange(id: string): void {

    this.selectedProjectId.set(id);

    this.loadRules();

    this.loadExecutions();

  }



  onTriggerChange(): void {

    const preset = defaultConditionForTrigger(this.triggerType);

    this.conditions = preset ? [{ ...preset }] : [];

  }



  addCondition(): void {

    this.conditions.push({ field: 'status', op: 'eq', value: '' });

  }



  removeCondition(index: number): void {

    this.conditions.splice(index, 1);

  }



  loadRules(): void {

    this.loading.set(true);

    this.error.set(null);

    const projectId = this.selectedProjectId() || undefined;

    this.automation.list(projectId).subscribe({

      next: (items) => {

        this.rules.set(Array.isArray(items) ? items : []);

        this.loading.set(false);

      },

      error: (err) => {

        this.error.set(err?.message || 'Failed to load automation rules.');

        this.rules.set([]);

        this.loading.set(false);

      }

    });

  }



  loadExecutions(): void {

    const projectId = this.selectedProjectId() || undefined;

    this.automation.listExecutions(projectId).subscribe({

      next: (items) => this.executions.set(Array.isArray(items) ? items : []),

      error: () => this.executions.set([])

    });

  }



  buildConditionsJson(): string | undefined {

    const rows = this.conditions.filter(c => c.field && c.value.trim());

    if (!rows.length) return undefined;

    return JSON.stringify({ conditions: rows });

  }



  buildActionsJson(): string {

    const config: Record<string, string> = {};

    switch (this.actionType) {

      case 'AssignEmployee':

        if (this.assignEmployeeId) config['employeeId'] = this.assignEmployeeId;

        break;

      case 'NotifyUser':

        if (this.notifyUserId) config['userId'] = this.notifyUserId;

        break;

      case 'SetField':

        if (this.actionFieldKey) config['fieldKey'] = this.actionFieldKey;

        if (this.actionFieldValue) config['value'] = this.actionFieldValue;

        break;

      case 'ChangeStatus':

        if (this.actionStatus) config['status'] = this.actionStatus;

        break;

      case 'MoveSprint':

        if (this.actionSprintId) config['sprintId'] = this.actionSprintId;

        break;

      case 'SendEmail':

        if (this.actionEmail) config['to'] = this.actionEmail;

        break;

      case 'Webhook':

        if (this.actionWebhookUrl) config['url'] = this.actionWebhookUrl;

        break;

      case 'ScheduleReminder':

        config['hoursBefore'] = this.actionReminderHours || '24';

        break;

      default:

        break;

    }

    return JSON.stringify([{ type: this.actionType, config }]);

  }



  createRule(): void {

    if (!this.ruleName.trim()) {

      this.error.set('Rule name is required.');

      return;

    }

    this.saving.set(true);

    this.automation.create({

      name: this.ruleName.trim(),

      description: this.ruleDescription.trim() || undefined,

      projectId: this.selectedProjectId() || undefined,

      triggerType: resolveBackendTriggerType(this.triggerType),

      conditionsJson: this.buildConditionsJson(),

      actionsJson: this.buildActionsJson(),

      isEnabled: true

    }).subscribe({

      next: () => {

        this.ruleName = '';

        this.ruleDescription = '';

        this.assignEmployeeId = '';

        this.notifyUserId = '';

        this.actionFieldKey = '';

        this.actionFieldValue = '';

        this.actionStatus = '';

        this.actionSprintId = '';

        this.actionEmail = '';

        this.actionWebhookUrl = '';

        this.conditions = [];

        this.saving.set(false);

        this.loadRules();

      },

      error: (err) => {

        this.error.set(err?.message || 'Failed to create rule.');

        this.saving.set(false);

      }

    });

  }



  toggleRule(rule: AutomationRuleDto): void {

    this.automation.update(rule.id, {

      name: rule.name,

      description: rule.description,

      triggerType: rule.triggerType,

      conditionsJson: rule.conditionsJson,

      actionsJson: rule.actionsJson || '[]',

      isEnabled: !rule.isEnabled

    }).subscribe({

      next: () => this.loadRules(),

      error: (err) => this.error.set(err?.message || 'Failed to update rule.')

    });

  }



  deleteRule(rule: AutomationRuleDto): void {

    if (!confirm(`Delete rule "${rule.name}"?`)) return;

    this.automation.delete(rule.id).subscribe({

      next: () => this.loadRules(),

      error: (err) => this.error.set(err?.message || 'Failed to delete rule.')

    });

  }



  triggerLabel(rule: AutomationRuleDto): string {

    return rule.triggerTypeLabel ?? this.triggerOptions().find(t => t.value === rule.triggerType)?.label ?? String(rule.triggerType);

  }



  actionLabel(rule: AutomationRuleDto): string {

    if (rule.actionsJson) {

      try {

        const parsed = JSON.parse(rule.actionsJson) as { type?: string }[];

        const first = parsed[0]?.type;

        if (first) {

          return this.actionOptions().find(a => a.value === first)?.label ?? first;

        }

      } catch { /* ignore */ }

    }

    return 'Action';

  }

}


